import { Component, inject } from '@angular/core';
import { BaseViewComponent } from '../base-view.component';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { LanguageStateService } from '../../common/services/language-state.service';
import { SecureDataService, WindstreamCustomerAccountModel } from '../../common/services/securedata.sevice';
import { StateChangedModel } from '../../common/services/state.service';
import { take } from 'rxjs';

@Component({
    selector: 'app-windstream-customer-account-setup',
    templateUrl: './windstream-customer-account-setup.component.html',
    styleUrl: './windstream-customer-account-setup.component.scss',
    standalone: false
})
export class WindstreamCustomerAccountSetupComponent extends BaseViewComponent {

  #secureDataService: SecureDataService = inject(SecureDataService)
  languageService: LanguageStateService = inject(LanguageStateService)
  fb: FormBuilder = inject(FormBuilder)
  customerAccountData: WindstreamCustomerAccountModel | undefined;
  securityInformationForm: FormGroup | undefined;
  showCancelDialog = false;

  protected override refreshView(): void {
    this.initFormGroup()
  }

  initFormGroup() {
    this.securityInformationForm = this.fb.group({
      accountPin: ['', [Validators.required]],
      confirmAccountPin: [''],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]],
      verificationCode: ['', [Validators.required]],
      confirmCode: [''],
      currentInternetProviderName: ['', [Validators.required]],
      windstreamReferralId: [''],
      comments: [''],
      authorizedUserFullName: ['', [Validators.required]],
      authorizedUserPhoneNumber: ['', [Validators.required], Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]
    });

    this.securityInformationForm.get('confirmAccountPin')?.setValidators([
      Validators.required,
      this.matchFieldValidator('accountPin')
    ]);

    this.securityInformationForm.get('confirmCode')?.setValidators([
      Validators.required,
      this.matchFieldValidator('verificationCode')
    ]);

    // Subscribe to changes to revalidate confirm fields
    this.securityInformationForm.get('accountPin')?.valueChanges.subscribe(() => {
      this.securityInformationForm.get('confirmAccountPin')?.updateValueAndValidity();
    });

    this.securityInformationForm.get('verificationCode')?.valueChanges.subscribe(() => {
      this.securityInformationForm.get('confirmCode')?.updateValueAndValidity();
    });
  }

  matchFieldValidator(fieldName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent) {
        return null;
      }

      const fieldToMatch = control.parent.get(fieldName);
      if (!fieldToMatch) {
        return null;
      }

      // Only validate if both fields have values
      if (control.value && fieldToMatch.value && control.value !== fieldToMatch.value) {
        return { mismatch: true };
      }

      return null;
    };
  }

  shouldShowError(controlName: string): boolean {
    const control = this.securityInformationForm.get(controlName);
    return !!(control?.touched && control?.errors);
  }

  formatPhone(event: any, controlName: string) {
    const control = this.securityInformationForm.get(controlName);
    if (!control) return;

    const input = event.target.value.replace(/\D/g, '').substring(0, 10);

    // Early return if no input
    if (!input) {
      control.setValue('', { emitEvent: false });
      return;
    }

    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6);

    let formattedValue = '';

    if (input.length > 6) {
      formattedValue = `${areaCode}-${middle}-${last}`;
    } else if (input.length > 3) {
      formattedValue = `${areaCode}-${middle}`;
    } else {
      formattedValue = areaCode;
    }

    // Only update if the value actually changed
    if (control.value !== formattedValue) {
      control.setValue(formattedValue, { emitEvent: false });

      // Set cursor position
      setTimeout(() => {
        const inputElement = event.target as HTMLInputElement;
        inputElement.setSelectionRange(formattedValue.length, formattedValue.length);
      });
    }
  }

  onKeyPressAllowOnlyDigits(event: KeyboardEvent): boolean {
    // Allow only digits (0-9) and control keys like backspace, delete, arrows
    const isDigit = /^\d$/.test(event.key)
    const isControlKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)

    return isDigit || isControlKey
  }

  sendCode() {
    // api call to send code
  }

  getErrorMessage(controlName: string): string {
    const control = this.securityInformationForm.get(controlName);
    if (!control || !control.errors) return '';

    // Required errors
    if (control.hasError('required')) {
      switch (controlName) {
        case 'accountPin':
          return 'Account Pin is required';
        case 'confirmAccountPin':
          return 'Confirm Account Pin is required';
        case 'phoneNumber':
          return 'Phone Number is required';
        case 'verificationCode':
          return 'Verification Code is required';
        case 'confirmCode':
          return 'Confirm Code is required';
        case 'currentInternetProviderName':
          return 'Current Internet Provider Name is required';
        case 'authorizedUserFullName':
          return 'Full Name is required';
        case 'authorizedUserPhoneNumber':
          return 'Phone Number is required';
        default:
          return 'This field is required';
      }
    }

    // Pattern errors
    if (control.hasError('pattern')) {
      switch (controlName) {
        case 'accountPin':
        case 'confirmAccountPin':
          return 'Account Pin must be 4 digits';
        case 'phoneNumber':
        case 'authorizedUserPhoneNumber':
          return 'Please enter a valid phone number';
        default:
          return 'Invalid format';
      }
    }

    // Mismatch errors
    if (control.hasError('mismatch')) {
      switch (controlName) {
        case 'confirmAccountPin':
          return 'Account Pins do not match';
        case 'confirmCode':
          return 'Verification Codes do not match';
        default:
          return 'Values do not match';
      }
    }

    return '';
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type === 'WindstreamCustomerAccountSetupInfo' && val.Data) {
      this.customerAccountData = val.Data as WindstreamCustomerAccountModel;
      if (this.customerAccountData) {
        this.securityInformationForm?.patchValue({
          currentInternetProviderName: this.customerAccountData.internetProvider
        });
      }
    }
  }

  onSubmitClicked() {
    if (this.securityInformationForm?.valid) {
      const model = this.securityInformationForm.value;
      this.#secureDataService.submitCustomerData(model, 'WindstreamCustomerAccountSetupInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.securityInformationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  onCancelClick() {
    this.showCancelDialog = true;
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('WindstreamCustomerAccountSetupInfo-Cancel').pipe(take(1)).subscribe({
        next: () => {
          this.navigateWaiting(true);
        },
        error: (err) => this.onCancelError('WindstreamCustomerAccountSetupInfo-Cancel', err)
      });
    }
  }

}
